import type { ToolUIMeta } from "@magnus/react";
import { builtInStaticToolRegistry } from "@magnus/react/tools";
import type { AllToolNames } from "@workspace/ai/config";
import { excelToolRegistry } from "./excel/registry";
import { outlookToolRegistry } from "./outlook/registry";
import { wordToolRegistry } from "./word/registry";

/**
 * Tools that intentionally render via the SDK's `GenericToolView` fallback and
 * so are exempt from the exhaustiveness guard below. This is grandfathered
 * coverage debt, not a licence to skip views: it names tools that already
 * shipped without a bespoke card. Adding a *new* server tool still requires
 * either a view (see the registries above) or a deliberate entry here — the
 * guard fails otherwise.
 *
 * `satisfies readonly AllToolNames[]` keeps the list honest: if one of these
 * tools is renamed or removed, or gains a view, the stale entry becomes a build
 * error until it's cleaned up.
 *
 * Current contents: the Microsoft Graph mail tools, which have no web card yet.
 */
const GENERIC_FALLBACK_TOOL_NAMES = [
  "copy_message",
  "create_draft",
  // Booking v1: createEvent's confirmation lives in the HITL approval card +
  // model narration; findRooms renders as narrated text. Bespoke cards are a
  // known follow-up.
  "createEvent",
  "findRooms",
  "listRoutines",
  "listWorkspaces",
  "create_forward_draft",
  "create_mail_folder",
  "create_reply_all_draft",
  "create_reply_draft",
  "get_mail_tips",
  "get_mailbox_settings",
  "get_message_mime",
  "stage_message_html",
  "list_folder_messages",
  "list_inbox_rules",
  "list_mail_categories",
  "list_mail_folders",
  "mail_batch",
  "move_message",
  "move_messages",
  "rename_mail_folder",
  "scan_inbox",
  "unsubscribe_from_email",
  "update_message",
  // OneNote writes: the HITL approval card previews the change and the model
  // narrates the result, so a bespoke card is a known follow-up. The six
  // OneNote *read* tools do have views (see the onenote-graph registry).
  "onenote_graph_create_page",
  "onenote_graph_update_page",
  "onenote_graph_create_section",
  "onenote_graph_create_notebook",
  "concurGetConfig",
  "concurSubmitReport",
  "concurListRecentCodings",
  "concurSearchCodings",
  "concurBatchUpdateExpenses",
  "concurFindFlightTickets",
  "freshserviceAgentMyTickets",
  "freshserviceAgentGetTicket",
  "freshserviceAgentSearchKnowledgeBase",
  "freshserviceAgentGetSolutionArticle",
  "freshserviceAgentCreateTicket",
  "freshserviceAgentAddNote",
  "freshserviceAgentListTickets",
  "freshserviceAgentListAgents",
  "freshserviceAgentListRequesters",
  "freshserviceAgentListAssets",
  "freshserviceAgentGetAsset",
  "freshserviceRequesterMyTickets",
  "freshserviceRequesterGetTicket",
  "freshserviceRequesterSearchKnowledgeBase",
  "freshserviceRequesterGetSolutionArticle",
  "freshserviceRequesterCreateTicket",
  "freshserviceRequesterReply",
  // CxAlloy (read-only): results are narrated from the formatted text; bespoke
  // SDK views are a known follow-up.
  "cxalloyListProjects",
  "cxalloyListIssues",
  "cxalloyListChecklists",
  "cxalloyListTests",
  "cxalloyListEquipment",
  "cxalloyListFiles",
  "cxalloyGetHistory",
  "cxalloyGetResource",
] as const satisfies readonly AllToolNames[];

type GenericFallbackToolName = (typeof GENERIC_FALLBACK_TOOL_NAMES)[number];

/**
 * Compile-time exhaustiveness guard — restores the develop-era check the SDK
 * extraction dropped. The SDK's static built-in views (`AllToolNames` keys)
 * plus this app's Office views must cover every server tool in `AllToolNames`
 * except those explicitly grandfathered into `GENERIC_FALLBACK_TOOL_NAMES`;
 * adding a tool to the capabilities config without a view here is a build error
 * (a missing key fails the `satisfies` below).
 *
 * MCP / progressive-disclosure tools carry `${capability}_${tool}` names that
 * aren't in `AllToolNames` — they live in the SDK's dynamic tier and fall back
 * to `GenericToolView` at runtime, so they're intentionally excluded here.
 *
 * This is purely a type-level assertion: actual runtime resolution lives in
 * the SDK's `ToolRenderer`, which merges the built-in + client-tool registries
 * and tolerates gaps via the generic fallback.
 */
export const staticToolViewExhaustiveness = {
  ...builtInStaticToolRegistry,
  ...excelToolRegistry,
  ...wordToolRegistry,
  ...outlookToolRegistry,
} satisfies Record<Exclude<AllToolNames, GenericFallbackToolName>, ToolUIMeta>;
